import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import Utils from "utils";
import openNotification from "utils/notification";
import OrderService from "services/OrderService";
import GooglePlacesServices from "services/GooglePlacesServices";

// DataPagingModel<OrderGridDto>
export const searchOrder = createAsyncThunk(
  "order/searchOrder",
  async (data, { rejectWithValue }) => {
    try {
      const response = await OrderService.searchOrder(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// OrderDto
export const getOrderDetail = createAsyncThunk(
  "order/getOrderDetail",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await OrderService.getOrderDetail(orderId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// List<OrderHistoryDto>
export const getOrderTimeline = createAsyncThunk(
  "order/getOrderTimeline",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await OrderService.getOrderTimeline(orderId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// (ReturnCode, OrderDto)
export const createOrder = createAsyncThunk(
  "order/createOrder",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await OrderService.createOrder(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// (ReturnCode, OrderDto)
export const updateOrder = createAsyncThunk(
  "order/updateOrder",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await OrderService.updateOrder(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// (ReturnCode, CancelRefundResultDto)
export const cancelOrder = createAsyncThunk(
  "order/cancelOrder",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await OrderService.cancelOrder(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// (ReturnCode, RecalculatePriceResultDto)
export const recalculatePrice = createAsyncThunk(
  "order/recalculatePrice",
  async (data, { rejectWithValue }) => {
    try {
      const response = await OrderService.recalculatePrice(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// (ReturnCode, CancelRefundResultDto)
export const getCancelPreview = createAsyncThunk(
  "order/getCancelPreview",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await OrderService.getCancelPreview(orderId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getAutoCompleteSearch = createAsyncThunk(
  "order/getAutoCompleteSearch",
  async (data, { rejectWithValue }) => {
    try {
      const response = await GooglePlacesServices.getAutoCompleteSearch(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getOrderDetailForMap = createAsyncThunk(
  "order/getOrderDetailForMap",
  async (orderId, { rejectWithValue }) => {
    try {
      const [detailRes, timelineRes] = await Promise.all([
        OrderService.getOrderDetail(orderId),
        OrderService.getOrderTimeline(orderId),
      ]);
      return {
        Detail: detailRes.data,
        Timeline: timelineRes.data,
      };
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getOrderCountByStatus = createAsyncThunk(
  "order/getOrderCountByStatus",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await OrderService.getOrderCountByStatus(params);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const recalculatePriceAsync = createAsyncThunk(
  "order/recalculatePriceAsync",
  async (data, { rejectWithValue }) => {
    try {
      const response = await OrderService.recalculatePrice(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const updateOrderAsync = createAsyncThunk(
  "order/updateOrderAsync",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await OrderService.updateOrder(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const markOrderRefunded = createAsyncThunk(
  "order/markOrderRefunded",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await OrderService.markRefund(payload);
      if (onSuccess) onSuccess(response);
      openNotification(
        "success",
        Utils.setLocale("admin.settings.popup.notification.success"),
        Utils.setLocale("admin.settings.popup.notification.title")
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const previewOrderPrice = createAsyncThunk(
  "order/previewOrderPrice",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await OrderService.previewPrice(payload);
      if (onSuccess) onSuccess(response);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

const initialState = {
  loading: false,
  loadingCount: false,
  orderList: [],
  orderDetail: null,
  orderTimeline: [],
  orderResult: null,
  cancelRefundResult: null,
  recalculatePriceResult: null,
  recalcResult:  null,
  recalcLoading: false,
  previewLoading: false,
  previewPrice: {},
  orderDetailForMap: {
    Detail: null,
    Timeline: [],
  },
  orderCountByStatus: {
    total: 0,
    confirmed: 0,      
    fullyAssigned: 0,  
    running: 0,       
    completed: 0,      
    cancelled: 0,     
  },
};

export const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    clearOrderDetail: (state) => {
      state.orderDetail = null;
    },
    clearOrderResult: (state) => {
      state.orderResult = null;
    },
    clearCancelRefundResult: (state) => {
      state.cancelRefundResult = null;
    },
    clearRecalculatePriceResult: (state) => {
      state.recalculatePriceResult = null;
    },
    clearRecalcResult: (state) => {
      state.recalcResult = null;
      state.recalcLoading = false;
    },
    clearPreviewPrice: (state) => {
      state.previewPrice = null;
      state.previewLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // searchOrder
      .addCase(searchOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderList = action.payload;
      })
      .addCase(searchOrder.rejected, (state) => {
        state.loading = false;
      })

      // getOrderDetail
      .addCase(getOrderDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrderDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetail = action.payload;
      })
      .addCase(getOrderDetail.rejected, (state) => {
        state.loading = false;
      })

      // getOrderTimeline
      .addCase(getOrderTimeline.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrderTimeline.fulfilled, (state, action) => {
        state.loading = false;
        state.orderTimeline = action.payload;
      })
      .addCase(getOrderTimeline.rejected, (state) => {
        state.loading = false;
      })

      // createOrder
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderResult = action.payload;
      })
      .addCase(createOrder.rejected, (state) => {
        state.loading = false;
      })

      // updateOrder
      .addCase(updateOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderResult = action.payload;
      })
      .addCase(updateOrder.rejected, (state) => {
        state.loading = false;
      })

      // cancelOrder
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.cancelRefundResult = action.payload;
      })
      .addCase(cancelOrder.rejected, (state) => {
        state.loading = false;
      })

      // recalculatePrice
      .addCase(recalculatePrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(recalculatePrice.fulfilled, (state, action) => {
        state.loading = false;
        state.recalculatePriceResult = action.payload;
      })
      .addCase(recalculatePrice.rejected, (state) => {
        state.loading = false;
      })

      // getCancelPreview
      .addCase(getCancelPreview.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCancelPreview.fulfilled, (state, action) => {
        state.loading = false;
        state.cancelRefundResult = action.payload;
      })
      .addCase(getCancelPreview.rejected, (state) => {
        state.loading = false;
      })

      // getOrderDetailForMap
      .addCase(getOrderDetailForMap.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrderDetailForMap.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetailForMap = action.payload;
      })
      .addCase(getOrderDetailForMap.rejected, (state) => {
        state.loading = false;
      })

      // getOrderCountByStatus
      .addCase(getOrderCountByStatus.pending, (state) => {
        state.loadingCount = true;
      })
      .addCase(getOrderCountByStatus.fulfilled, (state, action) => {
        state.loadingCount = false;
        state.orderCountByStatus =
          action.payload ?? initialState.orderCountByStatus;
      })
      .addCase(getOrderCountByStatus.rejected, (state) => {
        state.loadingCount = false;
      })

      // recalculatePriceAsync
      .addCase(recalculatePriceAsync.pending, (state) => {
        state.recalcLoading = true;
      })
      .addCase(recalculatePriceAsync.fulfilled, (state, action) => {
        state.recalcLoading = false;
        state.recalcResult = action.payload?.data ?? action.payload;
      })
      .addCase(recalculatePriceAsync.rejected, (state) => {
        state.recalcLoading = false;
      })

      // updateOrderAsync — dùng orderDetail để refresh page sau khi update
      .addCase(updateOrderAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOrderAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetail = action.payload?.data ?? action.payload;
      })
      .addCase(updateOrderAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(markOrderRefunded.pending, (state) => {
        state.loading = true;
      })
      .addCase(markOrderRefunded.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(markOrderRefunded.rejected, (state, action) => {
        state.loading = false;
      })

      .addCase(previewOrderPrice.pending, (state) => {
        state.previewLoading = true;
      })
      .addCase(previewOrderPrice.fulfilled, (state, action) => {
        state.previewLoading = false;
        state.previewPrice = action.payload?.data ?? action.payload;
      })
      .addCase(previewOrderPrice.rejected, (state, action) => {
        state.previewLoading = false;
      });
  },
});

export const {
  showLoading,
  clearOrderDetail,
  clearOrderResult,
  clearCancelRefundResult,
  clearRecalculatePriceResult,
  clearRecalcResult,
  clearPreviewPrice,
} = orderSlice.actions;

export default orderSlice.reducer;